import { createDraftSafeSelector } from '@reduxjs/toolkit';

import { Advice } from 'types/Checklist';
import type { RootState } from 'App/Store';
import ParkingForm from 'types/ParkingForm';
import { YesNoChoice } from 'types/Question';
import AdviceStatus from 'types/AdviceStatus';
import { ExtrasForm } from 'types/ExtrasForm';
import { PoliciesForm } from 'types/PoliciesForm';
import { BreakdownInfo } from 'types/BreakdownInfo';
import { SignatureForm } from 'types/SignatureForm';
import { RepairOrderForm } from 'types/RepairOrderForm';
import { CustomerInfoForm } from 'types/CustomerInfoForm';
import { VehicleCheckForm } from 'types/VehicleCheckForm';
import { PoliciesEnum } from 'modules/auth/types/Policies';
import { MobilityFormType } from 'types/MobilityDocumentFormType';
import { KioskPreferencesForm, QuestionsForm } from 'types/QuestionsForm';
import { VehicleCheckType } from 'modules/vehicleCheck/types/VehicleCheckModel';
import { SelfService, SelfServiceType } from 'modules/selfServices/types/SelfService';

import { KioskPreferencesQuestionsIds } from 'modules/selfServices/constants';

import formAdapter from 'modules/form/adapter';

import { getMFAData } from 'modules/auth/selectors';
import { getContext, getPolicies } from 'modules/dealers/selectors';
import { getSelectedSelfService, isQuickFlow } from 'modules/selfServices/selectors';

import { getProactiveWorks } from 'modules/selfServices/utils';
import { getDisplayName as getDisplayNameUtil, getExtras } from 'utils/formUtils';

import { FormState } from './types/FormState';
import FormKeys from './types/FormKeys';

const getProps = <T>(_: RootState, props: T) => props;

export const getState = (state: RootState) => state.form;

export const formAdapterSelectors = formAdapter.getSelectors<RootState>(getState);

export const getFormValuesByKey = createDraftSafeSelector(formAdapterSelectors.selectById, (form) => form?.values);
export const getFormValuesByKeys = createDraftSafeSelector(
  getProps,
  formAdapterSelectors.selectAll,
  (keys: Array<FormKeys>, forms) => keys?.map(
    (key) => forms.find((form) => form.key === key)?.values,
  ) ?? [],
);

type SavedFormType = {
  repairOrder: FormState<RepairOrderForm>;
  questions: FormState<QuestionsForm>;
  extras: FormState<ExtrasForm>;
  parking: FormState<ParkingForm>;
  policies: FormState<PoliciesForm>;
  signature: FormState<SignatureForm>;
  vehicleCheck: FormState<VehicleCheckForm>;
  mobilityParking: FormState<ParkingForm>;
  mobility: FormState<MobilityFormType>;
  breakdownInfo: FormState<BreakdownInfo>;
  kioskPreferencesQuestions?: FormState<KioskPreferencesForm>;
};

/**
 * Return the advices to send based, on self-service advices and form advices.
 * Sometimes, the self-service advices are not the same as the form advices.
 * Example:
 *   - User accepts/refuses some advices in the self-service
 *   - Dealership remove one of those advice at the same time
 *   - In this case, we need clean the advices that are still in the form.
 */
const getAdvicesToSend = (advices?: Array<Advice>, formAdvices?: Array<Advice>) => {
  const adviceIds = advices?.map((advice) => advice.adviceId) ?? [];
  return formAdvices?.filter(({ adviceId }) => adviceIds.includes(adviceId));
};

export const getFormattedFormData = createDraftSafeSelector(
  formAdapterSelectors.selectEntities,
  getPolicies,
  getProps,
  isQuickFlow,
  (forms, policies, selfService: SelfService, isQuick: boolean): Partial<SelfService> => {
    const { type } = selfService;
    if (type === SelfServiceType.CHECK_IN || type === SelfServiceType.BREAKDOWN) {
      // Formatters
      const {
        repairOrder,
        questions,
        extras: extrasForm,
        parking,
        signature,
        vehicleCheck,
        mobility,
        breakdownInfo,
        policies: policiesForm,
        kioskPreferencesQuestions,
      } = forms as Partial<SavedFormType>;

      const { extras } = extrasForm?.values ?? {};
      const hasParkingData = parking?.values?.spotNumber || parking?.values?.x >= 0;
      const { proactiveWorks/*  ...basketData */, ...repairOrderValues } = repairOrder?.values ?? {};
      const checkInKioskPreferences = kioskPreferencesQuestions?.values?.kioskPreferencesQuestions?.find(
        (question) => question.id === KioskPreferencesQuestionsIds.CHECK_IN,
      );
      const checkOutKioskPreferences = kioskPreferencesQuestions?.values?.kioskPreferencesQuestions?.find(
        (question) => question.id === KioskPreferencesQuestionsIds.CHECK_OUT,
      );

      return {
        ...repairOrderValues,
        ...(questions?.values && { questionForm: { questions: questions.values.questions } }),
        checklists: selfService.checklists?.map((checklist) => ({
          ...checklist,
          items: checklist.items?.map((item) => ({
            ...item,
            advices: getAdvicesToSend(item.advices, extras),
          })),
        })),
        proactiveChecklists: selfService.proactiveChecklists?.map((proactiveChecklist) => ({
          ...proactiveChecklist,
          items: proactiveChecklist.items?.map((item) => ({
            ...item,
            advices: getAdvicesToSend(item.advices, proactiveWorks),
          })),
        })),
        ...(hasParkingData && { parking: parking.values }),
        ...(mobility?.values && {
          mobility: {
            ...mobility.values,
            documents: mobility.values.documents?.map(({ media, ...document }) => ({
              ...document,
              media: mobility.values.customerAccepted ? media : undefined,
            })),
          },
        }),
        kioskPreference: {
          ...selfService.kioskPreference,
          ...(checkInKioskPreferences ? {
            checkInComment: checkInKioskPreferences.comment,
            checkIn: checkInKioskPreferences.response && checkInKioskPreferences.response === YesNoChoice.YES,
          } : {}),
          ...(checkOutKioskPreferences ? {
            checkOutComment: checkOutKioskPreferences.comment,
            checkOut: checkOutKioskPreferences.response && checkOutKioskPreferences.response === YesNoChoice.YES,
          } : {}),
        },
        signature: { uploadId: signature?.values?.uploadId },
        breakdownServiceEmail: signature?.values?.breakdownServiceEmail,
        vehicleCheckAccepted: vehicleCheck?.values?.vehicleCheck === YesNoChoice.YES,
        courtesyVehicleCheckAccepted: vehicleCheck?.values?.courtesyVehicleCheck === YesNoChoice.YES,
        status: selfService.status,
        type: selfService.type,
        isQuick,
        ...(breakdownInfo?.values && breakdownInfo.values),
        ...policiesForm?.values,
        policies: policiesForm?.values?.basketGTC ? [{
          link: policies.find((policy) => policy.type === PoliciesEnum.GENERAL_TERMS_CONDITION).link,
          type: PoliciesEnum.GENERAL_TERMS_CONDITION,
        }] : undefined,
      };
    }
    if (type === SelfServiceType.CHECK_OUT) {
      const { mobilityParking } = forms as Partial<SavedFormType>;
      const { mobility, signature } = forms as Partial<SavedFormType>;
      const hasParkingData = mobilityParking?.values?.spotNumber || mobilityParking?.values?.x >= 0;

      return {
        ...(hasParkingData ? {
          mobility: {
            ...mobility?.values,
            parkingReturn: mobilityParking.values,
          },
        } : {}),
        signature: {
          uploadId: signature?.values?.uploadId,
        },
      };
    }
    return {};
  },
);

const getVehicleCheckForm = createDraftSafeSelector(
  formAdapterSelectors.selectEntities,
  ({ vehicleCheck }) => vehicleCheck,
);
const getVehicleCheckFormValues = createDraftSafeSelector(
  getVehicleCheckForm,
  (vehicleCheckForm) => vehicleCheckForm?.values,
);

export const isVehicleCheckAccepted = createDraftSafeSelector(
  getVehicleCheckFormValues,
  (vehicleCheckFormValues) => vehicleCheckFormValues?.vehicleCheck === YesNoChoice.YES,
);

export const isCourtesyVehicleCheckAccepted = createDraftSafeSelector(
  getVehicleCheckFormValues,
  (vehicleCheckFormValues) => vehicleCheckFormValues?.courtesyVehicleCheck === YesNoChoice.YES,
);

export const isCourtesyVehicleCheckRefused = createDraftSafeSelector(
  getVehicleCheckFormValues,
  (vehicleCheckFormValues) => vehicleCheckFormValues?.courtesyVehicleCheck === YesNoChoice.NO,
);

export const getAcceptedVehicleChecks = createDraftSafeSelector(
  isVehicleCheckAccepted,
  isCourtesyVehicleCheckAccepted,
  (vehicleCheckAccepted, courtesyVehicleCheckAccepted) => [
    ...(vehicleCheckAccepted ? [VehicleCheckType.PERSONAL] : []),
    ...(courtesyVehicleCheckAccepted ? [VehicleCheckType.COURTESY] : []),
  ],
);

export const getVehicleCheckConfig = createDraftSafeSelector(
  getMFAData,
  getContext,
  isVehicleCheckAccepted,
  isCourtesyVehicleCheckAccepted,
  (mfaData, context, vehicleCheckAccepted, courtesyVehicleCheckAccepted) => ({
    vehicleCheckEnabled: mfaData?.vehicleCheckEnabled ?? context?.vehicleCheckEnabled ?? false,
    vehicleCheckAccepted,
    courtesyVehicleCheckEnabled: mfaData?.mobility?.courtesyVehicleCheckEnabled
                                 ?? context?.mobility?.courtesyVehicleCheckEnabled
                                 ?? false,
    courtesyVehicleCheckAccepted,
  }),
);

const getMobilityForm = createDraftSafeSelector(
  formAdapterSelectors.selectEntities,
  ({ mobility }) => mobility,
);

const getMobilityFormValues = createDraftSafeSelector(
  getMobilityForm,
  (mobilityForm) => mobilityForm?.values,
);

export const isMobilityAccepted = createDraftSafeSelector(
  getMobilityFormValues,
  (mobility) => mobility?.customerAccepted,
);

const getExtrasForm = createDraftSafeSelector(
  formAdapterSelectors.selectEntities,
  ({ extras }) => extras?.values,
);

const getAcceptedAdvices = (advices: Array<Advice>, formAdvices: Array<Advice>) =>
  // Check if some advice have been accepted during the self-service flow.
  advices?.filter(({ adviceId, status }) => {
    // Do not take into account extra that has been accepted in a previous check-in flow
    if (!status || status === AdviceStatus.PENDING) {
      const adviceStatus = formAdvices?.find((advice) => advice.adviceId === adviceId)?.status;
      return adviceStatus === AdviceStatus.ACCEPTED;
    }
    return false;
  }) ?? [];

export const getNewAcceptedExtra = createDraftSafeSelector(
  getExtrasForm,
  getSelectedSelfService,
  (form: ExtrasForm, selfService) => getAcceptedAdvices(getExtras(selfService), form?.extras),
);

const getRepairOrderForm = createDraftSafeSelector(
  formAdapterSelectors.selectEntities,
  ({ repairOrder }) => repairOrder?.values,
);

export const getNewAcceptedProactiveWorks = createDraftSafeSelector(
  getRepairOrderForm,
  getSelectedSelfService,
  (form: RepairOrderForm, selfService) => getAcceptedAdvices(getProactiveWorks(selfService), form?.proactiveWorks),
);

export const getCustomerInfoFormValues = createDraftSafeSelector(
  formAdapterSelectors.selectEntities,
  ({ customerInfo }) => customerInfo?.values as CustomerInfoForm,
);

export const getDisplayName = createDraftSafeSelector(getCustomerInfoFormValues, getDisplayNameUtil);
